package co.maytes.standupbot.dashboard.features.newstandup

import co.maytes.standupbot.dashboard.features.standups.ScheduleSpec
import co.maytes.standupbot.dashboard.features.standups.SlackDirectoryStore
import co.maytes.standupbot.dashboard.features.standups.StandupsStore
import co.maytes.standupbot.dashboard.features.templates.TemplatesStore
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.standup.CreateStandupRequestDto
import co.maytes.standupbot.shared.dto.standup.RosterSeedDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import kotlinx.coroutines.CoroutineScope

class NewStandupContainer(
    scope: CoroutineScope,
    private val standupsStore: StandupsStore,
    private val directory: SlackDirectoryStore,
    private val templates: TemplatesStore,
) : Container<NewStandupContract.State, NewStandupContract.Intent, NewStandupContract.Action> {

    override val store = scope.store<
            NewStandupContract.State,
            NewStandupContract.Intent,
            NewStandupContract.Action,
            >(initialState = NewStandupContract.State()) {
        configure(name = "NewStandup")
        logging()
        reduce { intent ->
            when (intent) {
                is NewStandupContract.Intent.Init -> init(intent.workspaceId, intent.defaultTimezone)
                is NewStandupContract.Intent.SetName -> updateState { copy(name = intent.value) }
                is NewStandupContract.Intent.SetChannelId -> updateState {
                    copy(
                        channelId = intent.value,
                        selectedSlackUserIds = if (intent.value == channelId) selectedSlackUserIds else emptySet(),
                    )
                }
                is NewStandupContract.Intent.SetMembers -> updateState { copy(selectedSlackUserIds = intent.slackUserIds) }
                is NewStandupContract.Intent.SetSchedule -> updateState { copy(schedule = intent.value) }
                is NewStandupContract.Intent.SetTemplateId -> updateState { copy(selectedTemplateId = intent.value) }
                NewStandupContract.Intent.OpenPicker -> updateState { copy(pickerOpen = true) }
                NewStandupContract.Intent.ClosePicker -> updateState { copy(pickerOpen = false) }
                is NewStandupContract.Intent.PickMembers -> updateState {
                    copy(
                        selectedSlackUserIds = (selectedSlackUserIds - intent.removes.toSet()) + intent.adds,
                        pickerOpen = false,
                    )
                }
                is NewStandupContract.Intent.RemoveMember -> updateState {
                    copy(selectedSlackUserIds = selectedSlackUserIds - intent.slackUserId)
                }
                NewStandupContract.Intent.Submit -> submit()
                NewStandupContract.Intent.Cancel ->
                    action(NewStandupContract.Action.Navigate("/app/standups"))
            }
        }
    }

    private suspend fun StoreScope<NewStandupContract.State, NewStandupContract.Action>.init(
        workspaceId: String,
        defaultTz: String,
    ) {
        if (workspaceId.isBlank()) return
        updateState {
            copy(
                workspaceId = workspaceId,
                schedule = if (schedule.timezone == "UTC") ScheduleSpec.default(defaultTz) else schedule,
            )
        }
        directory.refreshChannels(workspaceId)
        directory.refreshUsers(workspaceId)
        templates.refresh(workspaceId)
    }

    private suspend fun StoreScope<NewStandupContract.State, NewStandupContract.Action>.submit() {
        val state = currentState
        if (!state.canSubmit || state.workspaceId.isBlank()) return

        updateState { copy(creating = true) }
        val channelName = directory.channels.firstOrNull { it.id == state.channelId }?.name
        val created = standupsStore.create(
            workspaceId = state.workspaceId,
            body = CreateStandupRequestDto(
                name = state.name.trim(),
                slackChannelId = state.channelId,
                slackChannelName = channelName,
                scheduleCron = state.schedule.toCron(),
                timezone = state.schedule.timezone,
                templateId = state.selectedTemplateId,
                roster = RosterSeedDto(includeCreator = true, slackUserIds = state.selectedSlackUserIds.toList()),
            ),
        )
        when (created) {
            is Result.Ok -> {
                action(NewStandupContract.Action.Navigate("/app/standups/${created.value.id}"))
            }
            is Result.Err -> {
                updateState { copy(creating = false) }
                action(
                    NewStandupContract.Action.Toast(
                        "Create failed: ${created.error.displayMessage}",
                        error = true,
                    ),
                )
            }
        }
    }
}
